import React, { useMemo, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Card, Col, Form, InputGroup, ProgressBar, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import NavbarStandard from 'components/pages/landing/NavbarStandard';
import FooterStandard from 'components/pages/landing/FooterStandard';
import Section from 'components/common/Section';

const toNum = value => parseFloat(value) || 0;

const formatMoney = value => {
  if (!isFinite(value)) return '$ 0';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0
  }).format(value);
};

const formatPct = value => (isFinite(value) ? `${value.toFixed(1)} %` : '0 %');

const defaultFields = {
  productCost: '12000',
  packagingCost: '700',
  shippingCost: '0',
  otherCosts: '0',
  commissionPct: '13',
  installmentsPct: '0',
  adsPct: '6',
  taxPct: '3',
  fixedFeeMode: 'auto',
  fixedFee: '0',
  desiredMarginPct: '25',
  currentPrice: '24000'
};

const listingOptions = [
  { labelKey: 'LISTING_CLASSIC', commissionPct: '13', installmentsPct: '0' },
  { labelKey: 'LISTING_PREMIUM', commissionPct: '16', installmentsPct: '8.8' },
  { labelKey: 'LISTING_CUSTOM', commissionPct: '', installmentsPct: '' }
];

const getAutoFixedFee = price => {
  const numericPrice = toNum(price);
  if (numericPrice <= 0) return 0;
  if (numericPrice <= 15999) return 1255;
  if (numericPrice <= 23999) return 2500;
  if (numericPrice <= 33000) return 3030;
  return 0;
};

const solvePrice = ({ baseCost, variablePct, marginPct, fixedFeeMode, manualFixedFee }) => {
  let price = Math.max(baseCost * 1.5, 1);

  for (let i = 0; i < 10; i += 1) {
    const fixedFee = fixedFeeMode === 'auto' ? getAutoFixedFee(price) : manualFixedFee;
    const denominator = 1 - variablePct - marginPct;
    if (denominator <= 0.001) return { error: true };
    price = (baseCost + fixedFee) / denominator;
  }

  return {
    price,
    fixedFee: fixedFeeMode === 'auto' ? getAutoFixedFee(price) : manualFixedFee,
    error: false
  };
};

const calculateProfit = ({ price, baseCost, fixedFee, variablePct }) => {
  const variableCosts = price * variablePct;
  const totalCosts = baseCost + fixedFee + variableCosts;
  const profit = price - totalCosts;
  return {
    variableCosts,
    totalCosts,
    profit,
    marginPct: price > 0 ? (profit / price) * 100 : 0,
    roiPct: totalCosts > 0 ? (profit / totalCosts) * 100 : 0
  };
};

const Field = ({ label, value, onChange, prefix = '$', suffix, min = '0' }) => (
  <Form.Group className="mb-3">
    <Form.Label className="fw-semi-bold text-800">{label}</Form.Label>
    <InputGroup>
      {prefix && <InputGroup.Text>{prefix}</InputGroup.Text>}
      <Form.Control
        type="number"
        min={min}
        step="0.01"
        value={value}
        onChange={event => onChange(event.target.value)}
      />
      {suffix && <InputGroup.Text>{suffix}</InputGroup.Text>}
    </InputGroup>
  </Form.Group>
);

const Metric = ({ label, value, tone = 'dark', icon }) => (
  <div className="border rounded-3 p-3 h-100 bg-white">
    <div className="d-flex justify-content-between align-items-center mb-2">
      <span className="text-600 fs--1 fw-semi-bold">{label}</span>
      {icon && <FontAwesomeIcon icon={icon} className={`text-${tone}`} />}
    </div>
    <div className={`fs-1 fw-bold text-${tone}`}>{value}</div>
  </div>
);

const CostRow = ({ label, value }) => (
  <div className="d-flex justify-content-between align-items-center py-1 fs--1">
    <span className="text-700">{label}</span>
    <span className="fw-semi-bold text-900">{value}</span>
  </div>
);

const PublicCalculatorPage = () => {
  const { t } = useTranslation();
  const [entryMode, setEntryMode] = useState('guided');
  const [wizardStep, setWizardStep] = useState(0);
  const [fields, setFields] = useState(defaultFields);
  const tc = (key, options) => t(`PUBLIC_CALCULATOR.${key}`, options);

  const setField = (key, value) => {
    setFields(prev => ({ ...prev, [key]: value }));
  };

  const applyListingOption = option => {
    setFields(prev => ({
      ...prev,
      commissionPct: option.commissionPct,
      installmentsPct: option.installmentsPct
    }));
  };

  const results = useMemo(() => {
    const baseCost =
      toNum(fields.productCost) +
      toNum(fields.packagingCost) +
      toNum(fields.shippingCost) +
      toNum(fields.otherCosts);

    const variablePct =
      toNum(fields.commissionPct) / 100 +
      toNum(fields.installmentsPct) / 100 +
      toNum(fields.adsPct) / 100 +
      toNum(fields.taxPct) / 100;

    const marginPct = toNum(fields.desiredMarginPct) / 100;
    const manualFixedFee = toNum(fields.fixedFee);
    const target = solvePrice({
      baseCost,
      variablePct,
      marginPct,
      fixedFeeMode: fields.fixedFeeMode,
      manualFixedFee
    });
    const floor = solvePrice({
      baseCost,
      variablePct,
      marginPct: 0,
      fixedFeeMode: fields.fixedFeeMode,
      manualFixedFee
    });

    if (target.error || floor.error) {
      return { error: true };
    }

    const currentPrice = toNum(fields.currentPrice) || target.price;
    const currentFixedFee = fields.fixedFeeMode === 'auto' ? getAutoFixedFee(currentPrice) : manualFixedFee;
    const current = calculateProfit({
      price: currentPrice,
      baseCost,
      fixedFee: currentFixedFee,
      variablePct
    });
    const suggested = calculateProfit({
      price: target.price,
      baseCost,
      fixedFee: target.fixedFee,
      variablePct
    });

    return {
      baseCost,
      currentPrice,
      variablePct,
      suggestedPrice: target.price,
      breakEvenPrice: floor.price,
      fixedFee: target.fixedFee,
      currentFixedFee,
      suggested,
      current,
      roomToBreakEven: currentPrice - floor.price,
      error: false
    };
  }, [fields]);

  const guidedSteps = [
    {
      title: tc('STEP_COSTS_TITLE'),
      icon: 'box',
      content: (
        <Row>
          <Col md={6}>
            <Field label={tc('PRODUCT_COST')} value={fields.productCost} onChange={value => setField('productCost', value)} />
          </Col>
          <Col md={6}>
            <Field label={tc('PACKAGING_COST')} value={fields.packagingCost} onChange={value => setField('packagingCost', value)} />
          </Col>
          <Col md={6}>
            <Field label={tc('ABSORBED_SHIPPING')} value={fields.shippingCost} onChange={value => setField('shippingCost', value)} />
          </Col>
          <Col md={6}>
            <Field label={tc('OTHER_UNIT_COSTS')} value={fields.otherCosts} onChange={value => setField('otherCosts', value)} />
          </Col>
        </Row>
      )
    },
    {
      title: tc('STEP_FEES_TITLE'),
      icon: 'percentage',
      content: (
        <>
          <div className="d-flex flex-wrap gap-2 mb-3">
            {listingOptions.map(option => (
              <Button
                key={option.labelKey}
                variant={fields.commissionPct === option.commissionPct && fields.installmentsPct === option.installmentsPct ? 'primary' : 'outline-primary'}
                size="sm"
                className="rounded-pill"
                onClick={() => applyListingOption(option)}
              >
                {tc(option.labelKey)}
              </Button>
            ))}
          </div>
          <Row>
            <Col md={4}>
              <Field label={tc('COMMISSION')} value={fields.commissionPct} onChange={value => setField('commissionPct', value)} prefix="" suffix="%" />
            </Col>
            <Col md={4}>
              <Field label={tc('INSTALLMENTS_COST')} value={fields.installmentsPct} onChange={value => setField('installmentsPct', value)} prefix="" suffix="%" />
            </Col>
            <Col md={4}>
              <Field label={tc('ADS')} value={fields.adsPct} onChange={value => setField('adsPct', value)} prefix="" suffix="%" />
            </Col>
          </Row>
        </>
      )
    },
    {
      title: tc('STEP_TARGET_TITLE'),
      icon: 'bullseye',
      content: (
        <Row>
          <Col md={4}>
            <Field label={tc('DESIRED_MARGIN')} value={fields.desiredMarginPct} onChange={value => setField('desiredMarginPct', value)} prefix="" suffix="%" />
          </Col>
          <Col md={4}>
            <Field label={tc('ESTIMATED_TAXES')} value={fields.taxPct} onChange={value => setField('taxPct', value)} prefix="" suffix="%" />
          </Col>
          <Col md={4}>
            <Field label={tc('CURRENT_OR_TEST_PRICE')} value={fields.currentPrice} onChange={value => setField('currentPrice', value)} />
          </Col>
        </Row>
      )
    }
  ];
  const canShowResults = !results.error;

  return (
    <>
      <NavbarStandard solid />
      <main className="bg-light">
        <Section className="pt-8 pb-6">
          <Row className="align-items-center g-4">
            <Col lg={6}>
              <Badge bg="soft-primary" text="primary" className="rounded-pill mb-3 px-3 py-2">
                {tc('HERO_BADGE')}
              </Badge>
              <h1 className="display-5 fw-bold text-900 mb-3">
                {tc('HERO_TITLE')}
              </h1>
              <p className="lead text-700 mb-4">
                {tc('HERO_SUBTITLE')}
              </p>
              <div className="d-flex flex-wrap gap-2">
                <Button as={Link} to="/waitlist" variant="primary" size="lg" className="rounded-pill px-4">
                  {tc('FULL_CTA')}
                  <FontAwesomeIcon icon="chevron-right" className="ms-2 fs--1" />
                </Button>
                <Button as={Link} to="/" variant="outline-secondary" size="lg" className="rounded-pill px-4">
                  {tc('BACK_HOME')}
                </Button>
              </div>
            </Col>
            <Col lg={6}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                      <div className="fw-bold text-900">{tc('QUICK_VIEW')}</div>
                      <div className="fs--1 text-600">{tc('ARS_ESTIMATE')}</div>
                    </div>
                    <FontAwesomeIcon icon="calculator" className="text-primary fs-3" />
                  </div>
                  <Row className="g-3">
                    <Col sm={6}>
                      <Metric label={tc('SUGGESTED_PRICE')} value={canShowResults ? formatMoney(results.suggestedPrice) : tc('REVIEW_DATA')} tone="primary" icon="tag" />
                    </Col>
                    <Col sm={6}>
                      <Metric
                        label={tc('ESTIMATED_PROFIT')}
                        value={canShowResults ? formatMoney(results.suggested.profit) : '-'}
                        tone={canShowResults && results.suggested.profit >= 0 ? 'success' : 'danger'}
                        icon="chart-line"
                      />
                    </Col>
                    <Col sm={6}>
                      <Metric label={tc('NET_MARGIN')} value={canShowResults ? formatPct(results.suggested.marginPct) : '-'} tone="info" icon="percent" />
                    </Col>
                    <Col sm={6}>
                      <Metric label={tc('MIN_PRICE')} value={canShowResults ? formatMoney(results.breakEvenPrice) : '-'} tone="warning" icon="shield-alt" />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Section>

        <Section className="pb-7">
          <Row className="g-4">
            <Col lg={7}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white border-0 p-4 pb-0">
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                    <div>
                      <h2 className="h4 mb-1 text-900">{tc('FORM_TITLE')}</h2>
                      <p className="text-600 mb-0">{tc('FORM_SUBTITLE')}</p>
                    </div>
                    <ButtonGroup size="sm">
                      <Button variant={entryMode === 'guided' ? 'primary' : 'outline-primary'} onClick={() => setEntryMode('guided')}>
                        {tc('GUIDED')}
                      </Button>
                      <Button variant={entryMode === 'advanced' ? 'primary' : 'outline-primary'} onClick={() => setEntryMode('advanced')}>
                        {tc('ADVANCED')}
                      </Button>
                    </ButtonGroup>
                  </div>
                </Card.Header>
                <Card.Body className="p-4">
                  {entryMode === 'guided' ? (
                    <>
                      <div className="d-flex align-items-center gap-3 mb-3">
                        <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
                          <FontAwesomeIcon icon={guidedSteps[wizardStep].icon} />
                        </div>
                        <div className="flex-1">
                          <div className="fs--1 text-600">{tc('STEP_PROGRESS', { current: wizardStep + 1, total: guidedSteps.length })}</div>
                          <h3 className="h5 mb-0 text-900">{guidedSteps[wizardStep].title}</h3>
                        </div>
                      </div>
                      <ProgressBar now={((wizardStep + 1) / guidedSteps.length) * 100} className="mb-4" style={{ height: 6 }} />
                      {guidedSteps[wizardStep].content}
                      <div className="d-flex justify-content-between mt-2">
                        <Button
                          variant="outline-secondary"
                          disabled={wizardStep === 0}
                          onClick={() => setWizardStep(step => Math.max(0, step - 1))}
                        >
                          <FontAwesomeIcon icon="chevron-left" className="me-2" />
                          {tc('BACK')}
                        </Button>
                        <Button
                          variant="primary"
                          disabled={wizardStep === guidedSteps.length - 1}
                          onClick={() => setWizardStep(step => Math.min(guidedSteps.length - 1, step + 1))}
                        >
                          {tc('NEXT')}
                          <FontAwesomeIcon icon="chevron-right" className="ms-2" />
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <Row>
                        <Col md={6}>
                          <Field label={tc('PRODUCT_COST')} value={fields.productCost} onChange={value => setField('productCost', value)} />
                        </Col>
                        <Col md={6}>
                          <Field label={tc('PACKAGING_COST')} value={fields.packagingCost} onChange={value => setField('packagingCost', value)} />
                        </Col>
                        <Col md={6}>
                          <Field label={tc('ABSORBED_SHIPPING_SHORT')} value={fields.shippingCost} onChange={value => setField('shippingCost', value)} />
                        </Col>
                        <Col md={6}>
                          <Field label={tc('OTHER_COSTS')} value={fields.otherCosts} onChange={value => setField('otherCosts', value)} />
                        </Col>
                        <Col md={4}>
                          <Field label={tc('COMMISSION')} value={fields.commissionPct} onChange={value => setField('commissionPct', value)} prefix="" suffix="%" />
                        </Col>
                        <Col md={4}>
                          <Field label={tc('INSTALLMENTS')} value={fields.installmentsPct} onChange={value => setField('installmentsPct', value)} prefix="" suffix="%" />
                        </Col>
                        <Col md={4}>
                          <Field label={tc('ADS')} value={fields.adsPct} onChange={value => setField('adsPct', value)} prefix="" suffix="%" />
                        </Col>
                        <Col md={4}>
                          <Field label={tc('TAXES')} value={fields.taxPct} onChange={value => setField('taxPct', value)} prefix="" suffix="%" />
                        </Col>
                        <Col md={4}>
                          <Field label={tc('TARGET_MARGIN')} value={fields.desiredMarginPct} onChange={value => setField('desiredMarginPct', value)} prefix="" suffix="%" />
                        </Col>
                        <Col md={4}>
                          <Field label={tc('CURRENT_PRICE')} value={fields.currentPrice} onChange={value => setField('currentPrice', value)} />
                        </Col>
                      </Row>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semi-bold text-800">{tc('FIXED_FEE')}</Form.Label>
                        <div className="d-flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant={fields.fixedFeeMode === 'auto' ? 'primary' : 'outline-primary'}
                            onClick={() => setField('fixedFeeMode', 'auto')}
                          >
                            {tc('AUTO_ESTIMATE')}
                          </Button>
                          <Button
                            size="sm"
                            variant={fields.fixedFeeMode === 'manual' ? 'primary' : 'outline-primary'}
                            onClick={() => setField('fixedFeeMode', 'manual')}
                          >
                            {tc('MANUAL')}
                          </Button>
                        </div>
                      </Form.Group>
                      {fields.fixedFeeMode === 'manual' && (
                        <Field label={tc('MANUAL_FIXED_FEE')} value={fields.fixedFee} onChange={value => setField('fixedFee', value)} />
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={5}>
              <Card className="border-0 shadow-sm mb-3">
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h2 className="h4 mb-1 text-900">{tc('RESULT_TITLE')}</h2>
                      <p className="text-600 mb-0 fs--1">{tc('RESULT_SUBTITLE')}</p>
                    </div>
                    <Badge bg="soft-success" text="success" className="rounded-pill">{tc('LOCAL_BADGE')}</Badge>
                  </div>

                  {results.error ? (
                    <Alert variant="warning" className="mb-0">
                      {tc('ERROR_PERCENTAGES')}
                    </Alert>
                  ) : (
                    <>
                      <div className="bg-100 rounded-3 p-3 mb-3">
                        <div className="fs--1 text-600 mb-1">{tc('SUGGESTED_PRICE')}</div>
                        <div className="display-6 fw-bold text-primary">{formatMoney(results.suggestedPrice)}</div>
                        <div className="text-700 fs--1 mt-1">
                          {tc('ESTIMATED_MARGIN', { margin: formatPct(results.suggested.marginPct) })}
                        </div>
                      </div>
                      <CostRow label={tc('BASE_COST')} value={formatMoney(results.baseCost)} />
                      <CostRow label={tc('ESTIMATED_FIXED_FEE')} value={formatMoney(results.fixedFee)} />
                      <CostRow label={tc('VARIABLE_COSTS')} value={formatMoney(results.suggested.variableCosts)} />
                      <CostRow label={tc('TOTAL_COSTS')} value={formatMoney(results.suggested.totalCosts)} />
                      <hr className="my-2" />
                      <CostRow label={tc('NET_PROFIT')} value={formatMoney(results.suggested.profit)} />
                      <CostRow label={tc('ESTIMATED_ROI')} value={formatPct(results.suggested.roiPct)} />
                      <CostRow label={tc('BREAK_EVEN')} value={formatMoney(results.breakEvenPrice)} />
                      <CostRow
                        label={tc('CURRENT_VS_MIN')}
                        value={`${results.roomToBreakEven >= 0 ? '+' : '-'}${formatMoney(Math.abs(results.roomToBreakEven))}`}
                      />
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Section>
      </main>
      <FooterStandard />
    </>
  );
};

export default PublicCalculatorPage;
